import { useRef, useState } from 'react';
import type { User, GearInfo, RidingStyle } from '@ridehub/shared';
import { Button } from '../../components/Button.js';
import { Input } from '../../components/Input.js';
import { apiClient } from '../../lib/api-client.js';
import {
  MAX_GEAR_FIELD_LENGTH,
  RIDING_STYLE_OPTIONS,
  MONTHLY_DISTANCE_OPTIONS,
  AREA_OPTIONS,
} from '@ridehub/shared';

interface ProfileEditFormProps {
  user: User;
  onSave: (updated: User) => void;
  onCancel: () => void;
}

const GEAR_FIELDS: { field: keyof GearInfo; label: string; placeholder: string }[] = [
  { field: 'frameModel', label: 'フレーム/モデル', placeholder: '例: Specialized Tarmac SL8' },
  { field: 'component', label: 'コンポーネント', placeholder: '例: Shimano Ultegra Di2 12s' },
  { field: 'wheel', label: 'ホイール', placeholder: '例: ZIPP 303 Firecrest' },
  { field: 'tire', label: 'タイヤ', placeholder: '例: Continental GP5000 28c' },
  { field: 'pedalShoes', label: 'ペダル・シューズ', placeholder: '例: SPD-SL / SH-RC903' },
  {
    field: 'cycleComputer',
    label: 'サイクルコンピューター',
    placeholder: '例: Garmin Edge 840 / Wahoo Elemnt Bolt',
  },
  { field: 'lights', label: 'フロント/リアライト', placeholder: '例: Lezyne Mega Drive 1800' },
  {
    field: 'helmetEyewear',
    label: 'ヘルメット/アイウェア',
    placeholder: '例: KASK Protone / Oakley Sutro',
  },
  {
    field: 'favoriteWearBrand',
    label: 'お気に入りウェアブランド',
    placeholder: '例: Rapha, Castelli, Isadore',
  },
  { field: 'smartWatch', label: 'スマートウォッチ', placeholder: '例: Apple Watch, Garmin Fenix' },
  { field: 'other', label: 'その他', placeholder: '' },
];

export function ProfileEditForm({ user, onSave, onCancel }: ProfileEditFormProps) {
  const [nickname, setNickname] = useState(user.nickname);
  const [bio, setBio] = useState(user.bio ?? '');
  const [avatarUrl, setAvatarUrl] = useState(user.avatarUrl);
  const [bikePhotoUrl, setBikePhotoUrl] = useState(user.bikePhotoUrl);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [bikePhotoUploading, setBikePhotoUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [gearInfo, setGearInfo] = useState<GearInfo>(user.gearInfo ?? {});
  const [ridingStyle, setRidingStyle] = useState<RidingStyle>(user.ridingStyle ?? {});
  const [activityAreas, setActivityAreas] = useState<string[]>(user.activityAreas ?? []);

  const avatarInputRef = useRef<HTMLInputElement>(null);
  const bikePhotoInputRef = useRef<HTMLInputElement>(null);

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const result = await apiClient.postForm<{ avatarUrl: string }>('/users/me/avatar', formData);
      setAvatarUrl(result.avatarUrl);
    } catch {
      setError('アバター画像のアップロードに失敗しました');
    } finally {
      setAvatarUploading(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const handleBikePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBikePhotoUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const result = await apiClient.postForm<{ bikePhotoUrl: string }>(
        '/users/me/bike-photo',
        formData
      );
      setBikePhotoUrl(result.bikePhotoUrl);
    } catch {
      setError('愛車写真のアップロードに失敗しました');
    } finally {
      setBikePhotoUploading(false);
      if (bikePhotoInputRef.current) bikePhotoInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const updated = await apiClient.put<User>(
        '/users/me',
        {
          nickname,
          bio: bio || undefined,
          gearInfo,
          ridingStyle,
          activityAreas: activityAreas.length > 0 ? activityAreas : undefined,
        },
        { requireAuth: true }
      );
      if (updated) onSave({ ...updated, avatarUrl, bikePhotoUrl });
    } catch {
      setError('プロフィールの更新に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      {/* アバターセクション */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-2">アイコン画像</p>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-gray-200 rounded-full overflow-hidden flex-shrink-0">
            {avatarUrl ? (
              <img src={avatarUrl} alt="アバター" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400 text-2xl">
                👤
              </div>
            )}
          </div>
          <div>
            <button
              type="button"
              onClick={() => avatarInputRef.current?.click()}
              disabled={avatarUploading}
              className="text-sm text-primary font-medium disabled:opacity-50"
            >
              {avatarUploading ? 'アップロード中...' : '画像を変更'}
            </button>
            <p className="text-xs text-gray-400 mt-0.5">JPEG / PNG / WebP・5MB以内</p>
          </div>
          <input
            ref={avatarInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleAvatarChange}
            className="hidden"
          />
        </div>
      </div>

      {/* 基本情報 */}
      <Input
        label="ニックネーム"
        value={nickname}
        onChange={(e) => setNickname(e.target.value)}
        required
        maxLength={50}
      />
      <div>
        <label className="text-sm font-medium text-gray-700 block mb-1">自己紹介</label>
        <textarea
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          maxLength={500}
          rows={3}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {/* ギア紹介 */}
      <div className="border-t border-gray-100 pt-4 flex flex-col gap-3">
        <p className="text-sm font-medium text-gray-700">ギア紹介</p>
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">愛車写真</p>
          {bikePhotoUrl && (
            <img
              src={bikePhotoUrl}
              alt="愛車"
              className="w-full rounded-lg mb-2 max-h-48 object-cover"
            />
          )}
          <button
            type="button"
            onClick={() => bikePhotoInputRef.current?.click()}
            disabled={bikePhotoUploading}
            className="text-sm text-primary font-medium disabled:opacity-50"
          >
            {bikePhotoUploading ? 'アップロード中...' : bikePhotoUrl ? '写真を変更' : '写真を追加'}
          </button>
          <p className="text-xs text-gray-400 mt-0.5">JPEG / PNG / WebP・5MB以内</p>
          <input
            ref={bikePhotoInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleBikePhotoChange}
            className="hidden"
          />
        </div>
        {GEAR_FIELDS.map(({ field, label, placeholder }) => (
          <Input
            key={field}
            label={label}
            value={gearInfo[field] ?? ''}
            onChange={(e) =>
              setGearInfo((prev: GearInfo) => ({
                ...prev,
                [field]: e.target.value || undefined,
              }))
            }
            maxLength={MAX_GEAR_FIELD_LENGTH}
            placeholder={placeholder}
          />
        ))}
      </div>

      {/* 活動エリア */}
      <div className="border-t border-gray-100 pt-4 flex flex-col gap-3">
        <p className="text-sm font-medium text-gray-700">活動エリア</p>
        <p className="text-xs text-gray-500 -mt-2">主に走るエリアをすべて選択</p>
        <div className="flex flex-col gap-3">
          {AREA_OPTIONS.map(({ group, areas }) => (
            <div key={group}>
              <p className="text-xs font-medium text-gray-400 mb-1.5">{group}</p>
              <div className="flex flex-wrap gap-1.5">
                {areas.map((area) => {
                  const selected = activityAreas.includes(area);
                  return (
                    <button
                      key={area}
                      type="button"
                      onClick={() =>
                        setActivityAreas((prev) =>
                          selected ? prev.filter((p) => p !== area) : [...prev, area]
                        )
                      }
                      className={[
                        'text-xs px-2.5 py-1 rounded-full border transition-colors',
                        selected
                          ? 'border-primary bg-primary-50 text-primary font-medium'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300',
                      ].join(' ')}
                    >
                      {area}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        {activityAreas.length > 0 && (
          <button
            type="button"
            onClick={() => setActivityAreas([])}
            className="text-xs text-gray-400 text-left"
          >
            選択を解除
          </button>
        )}
      </div>

      {/* 走行スタイル */}
      <div className="border-t border-gray-100 pt-4 flex flex-col gap-3">
        <p className="text-sm font-medium text-gray-700">走行スタイル</p>
        <div>
          <p className="text-xs text-gray-500 mb-2">当てはまるものをすべて選択</p>
          <div className="flex flex-col gap-2">
            {RIDING_STYLE_OPTIONS.map(({ label, description }) => {
              const checked = ridingStyle.styles?.includes(label) ?? false;
              return (
                <label key={label} className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) =>
                      setRidingStyle((prev: RidingStyle) => {
                        const current = prev.styles ?? [];
                        const next = e.target.checked
                          ? [...current, label]
                          : current.filter((s) => s !== label);
                        return { ...prev, styles: next.length > 0 ? next : undefined };
                      })
                    }
                    className="w-4 h-4 mt-0.5 flex-shrink-0 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <div>
                    <span className="text-sm text-gray-700">{label}</span>
                    <p className="text-xs text-gray-400 mt-0.5">{description}</p>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">月間走行距離</p>
          <div className="flex flex-col gap-2">
            {MONTHLY_DISTANCE_OPTIONS.map(({ label, description }) => (
              <label key={label} className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="radio"
                  name="monthlyDistance"
                  value={label}
                  checked={ridingStyle.monthlyDistance === label}
                  onChange={() =>
                    setRidingStyle((prev: RidingStyle) => ({ ...prev, monthlyDistance: label }))
                  }
                  className="w-4 h-4 mt-0.5 flex-shrink-0 border-gray-300 text-primary focus:ring-primary"
                />
                <div>
                  <span className="text-sm text-gray-700">{label}</span>
                  <p className="text-xs text-gray-400 mt-0.5">{description}</p>
                </div>
              </label>
            ))}
            {ridingStyle.monthlyDistance && (
              <button
                type="button"
                onClick={() =>
                  setRidingStyle((prev: RidingStyle) => ({ ...prev, monthlyDistance: undefined }))
                }
                className="text-xs text-gray-400 text-left mt-1"
              >
                選択を解除
              </button>
            )}
          </div>
        </div>
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}
      <div className="flex gap-2">
        <Button type="submit" loading={loading}>
          保存
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          キャンセル
        </Button>
      </div>
    </form>
  );
}
